import React from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ReceiptText, ArrowRight, Building2 } from 'lucide-react'

const ReturnTypeSelection = () => {
  const navigate = useNavigate()

  const handleSelection = (returnType) => {
    localStorage.setItem('SELECTED_RETURN_TYPE', returnType)
    if (returnType === 'tds-return') {
      navigate('/tds-dashboard/start')
    } else if (returnType === 'gst-return') {
      navigate('/gst-dashboard/gstr7')
    }
  }

  return (
    <div className="min-h-screen relative flex items-center justify-center overflow-hidden bg-slate-900 selection:bg-purple-500 selection:text-white">
      {/* 1. Base Liquid Mesh Background (Matching Welcome Page) */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_50%,rgba(17,24,39,1),rgba(0,0,0,1))]"></div>
        <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] bg-purple-600 rounded-full mix-blend-multiply filter blur-[128px] opacity-30 animate-blob"></div>
        <div className="absolute top-[20%] right-[-10%] w-[50%] h-[50%] bg-cyan-600 rounded-full mix-blend-multiply filter blur-[128px] opacity-30 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-[-20%] left-[20%] w-[50%] h-[50%] bg-pink-600 rounded-full mix-blend-multiply filter blur-[128px] opacity-30 animate-blob animation-delay-4000"></div>
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")` }}></div>
      </div>

      <div className="max-w-7xl w-full relative z-10 px-6 py-12 flex flex-col items-center">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16 space-y-4"
        >
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-transparent bg-clip-text bg-gradient-to-br from-white via-white/90 to-white/50 drop-shadow-lg">
            Select Filing Type
          </h1>
          <p className="text-lg md:text-xl text-white/60 font-medium tracking-wide max-w-2xl mx-auto">
            Choose your path. Our automated tools handle the rest.
          </p>
        </motion.div>

        {/* Cards Container */}
        <div className="grid md:grid-cols-2 gap-8 w-full max-w-5xl">
          {/* TDS Card */}
          <GlassCard
            title="TDS Return"
            description="File quarterly 24Q, 26Q returns. Generate compliant FVU files & manage records."
            icon={ReceiptText}
            accentColor="from-cyan-400 to-blue-500"
            onClick={() => handleSelection('tds-return')}
            delay={0.1}
          />

          {/* GST Card */}
          <GlassCard
            title="GST Return"
            description="Automate GSTR filings (1, 7, 3B). Sync directly with the portal for error-free uploads."
            icon={Building2}
            accentColor="from-fuchsia-400 to-purple-500"
            onClick={() => handleSelection('gst-return')}
            delay={0.2}
          />
        </div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 1 }}
          className="mt-16 text-center"
        >
          <p className="text-white/30 text-sm">
            Need help? <a href="tel:+919958229051" className="text-white/70 hover:text-white cursor-pointer transition-colors underline decoration-white/30 underline-offset-4">Contact Support: +91-9958229051</a>
          </p>
        </motion.div>
      </div>
    </div>
  )
}

// Reusable Glass Card Component
const GlassCard = ({ title, description, icon: Icon, accentColor, onClick, delay }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5 }}
      whileHover={{ y: -5, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="group relative overflow-hidden rounded-[2.5rem] bg-white/5 backdrop-blur-xl border border-white/10 p-10 cursor-pointer transition-all duration-300 shadow-2xl shadow-black/20 hover:bg-white/10 hover:border-white/20 hover:shadow-cyan-500/10"
    >
      {/* Hover Gradient Glow */}
      <div className={`absolute inset-0 bg-gradient-to-br ${accentColor} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>

      <div className="relative z-10 flex flex-col h-full">
        {/* Icon Circle */}
        <div className={`w-20 h-20 rounded-3xl bg-gradient-to-br ${accentColor} p-0.5 mb-8 shadow-lg group-hover:shadow-${accentColor.split('-')[1]}-500/30 transition-shadow duration-300`}>
          <div className="w-full h-full bg-black/40 backdrop-blur-md rounded-[22px] flex items-center justify-center">
            <Icon className="w-10 h-10 text-white" />
          </div>
        </div>

        <div className="flex-1">
          <h3 className="text-4xl font-bold text-white mb-4 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-white/70 transition-all">
            {title}
          </h3>
          <p className="text-white/50 text-lg leading-relaxed group-hover:text-white/70 transition-colors">
            {description}
          </p>
        </div>

        {/* Arrow Button */}
        <div className="mt-10 flex items-center justify-between text-white/50 group-hover:text-white font-medium transition-colors">
          <span className="text-xl">Start Application</span>
          <div className={`p-3 rounded-full bg-white/5 group-hover:bg-gradient-to-r ${accentColor} transition-all duration-300 shadow-inner`}>
            <ArrowRight className="w-6 h-6 group-hover:text-white transition-colors" />
          </div>
        </div>
      </div>
    </motion.div>
  )
}

export default ReturnTypeSelection
